import React, { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { fetchAdminPosts, setPostVisibility } from '../../lib/api';

const SevagramModeration = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => {
    fetchAdminPosts().then(setData).catch(() => setData({ count: 0, posts: [] }));
  };

  useEffect(() => { load(); }, []);

  const toggle = async (post) => {
    setBusy(post.id);
    try {
      await setPostVisibility(post.id, !post.visible);
      setData((prev) => ({
        ...prev,
        posts: prev.posts.map((p) =>
          p.id === post.id ? { ...p, visible: !post.visible } : p
        ),
      }));
    } finally {
      setBusy(null);
    }
  };

  if (!data)
    return <div data-testid="mod-loading" className="h-40 bg-[#C1D7E2]/40 animate-pulse" />;

  return (
    <div data-testid="moderation-view" className="space-y-10">
      <header className="border-b border-[#C1D7E2] pb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-2">
            Sevagram Filter
          </p>
          <h3 className="font-serif text-3xl md:text-4xl text-[#7C9CB1] font-light">
            Community posts · visibility
          </h3>
          <p className="mt-2 font-serif italic text-[#90B0BF] max-w-2xl">
            Toggle a post's visibility without destroying it. Hidden posts no
            longer appear on the public Sevagram feed but remain in the creator
            dashboard for appeal.
          </p>
        </div>
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#90B0BF]">
          {data.count} posts
        </p>
      </header>

      <div data-testid="moderation-grid" className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {data.posts.map((p, i) => (
          <article
            key={p.id}
            data-testid={`mod-post-${i}`}
            className={`group border p-5 flex gap-4 ${
              p.visible
                ? 'border-[#C1D7E2] bg-[#F4F8F9]'
                : 'border-[#90B0BF] bg-[#C1D7E2]/30'
            }`}
          >
            <img
              src={p.media_url}
              alt=""
              className={`w-24 h-24 object-cover border border-[#C1D7E2] ${
                p.visible ? '' : 'opacity-60'
              }`}
            />
            <div className="flex-1 min-w-0">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#90B0BF]">
                {p.platform} · @{p.creator_handle}
              </p>
              <p className="font-serif text-base text-[#7C9CB1] mt-1 line-clamp-3">
                {p.caption}
              </p>
              <div className="mt-3 flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] text-[#90B0BF]">
                  {p.views || 0} views · {p.orders_attributed || 0} orders
                </span>
                <button
                  onClick={() => toggle(p)}
                  disabled={busy === p.id}
                  data-testid={`mod-toggle-${i}`}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase border transition-colors disabled:opacity-60 ${
                    p.visible
                      ? 'border-[#7C9CB1] text-[#7C9CB1] hover:bg-[#C1D7E2]/40'
                      : 'border-[#7C9CB1] bg-[#7C9CB1] text-[#F4F8F9]'
                  }`}
                >
                  {p.visible ? (
                    <>
                      <Eye size={12} strokeWidth={1.5} /> Visible
                    </>
                  ) : (
                    <>
                      <EyeOff size={12} strokeWidth={1.5} /> Hidden
                    </>
                  )}
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default SevagramModeration;
